/**
 * Prepare a user-picked image for Supabase Storage.
 * Mobile Safari often fails with "No content provided" when uploading File/Blob
 * directly — ArrayBuffer works reliably. Also normalizes HEIC/empty types to JPEG.
 */
export async function prepareImageForStorageUpload(file: File): Promise<{
  body: ArrayBuffer;
  contentType: string;
  ext: string;
}> {
  if (!file || file.size <= 0) {
    throw new Error('Selected file is empty. Please try another photo.');
  }

  const rawType = (file.type || '').toLowerCase().trim();
  const nameExt = file.name.split('.').pop()?.toLowerCase() || '';
  const looksHeic =
    rawType.includes('heic') ||
    rawType.includes('heif') ||
    nameExt === 'heic' ||
    nameExt === 'heif';

  // iPhone Photos often hand HEIC (or empty type). Convert so Storage + browsers agree.
  if (looksHeic || (!rawType && !['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(nameExt))) {
    const jpeg = await rasterizeImageFileToJpeg(file);
    const body = await jpeg.arrayBuffer();
    if (!body.byteLength) {
      throw new Error('Could not convert this photo. Try exporting as JPG, or pick another image.');
    }
    return { body, contentType: 'image/jpeg', ext: 'jpg' };
  }

  let ext = nameExt;
  if (!['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext)) {
    if (rawType.includes('png')) ext = 'png';
    else if (rawType.includes('webp')) ext = 'webp';
    else if (rawType.includes('gif')) ext = 'gif';
    else ext = 'jpg';
  }
  if (ext === 'jpeg') ext = 'jpg';

  const contentType =
    rawType === 'image/jpg'
      ? 'image/jpeg'
      : rawType.startsWith('image/')
        ? rawType
        : ext === 'jpg'
          ? 'image/jpeg'
          : `image/${ext}`;

  const body = await file.arrayBuffer();
  if (!body.byteLength) {
    throw new Error('Could not read the photo. Please try again or choose a different image.');
  }

  return { body, contentType, ext };
}

async function rasterizeImageFileToJpeg(file: File, quality = 0.9): Promise<Blob> {
  const objectUrl = URL.createObjectURL(file);
  try {
    let bitmap: ImageBitmap;
    try {
      bitmap = await createImageBitmap(file);
    } catch {
      const img = await loadHtmlImage(objectUrl);
      bitmap = await createImageBitmap(img);
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, bitmap.width);
    canvas.height = Math.max(1, bitmap.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      throw new Error('Could not process this photo on this device.');
    }
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (result) =>
          result ? resolve(result) : reject(new Error('Could not convert photo to JPEG.')),
        'image/jpeg',
        quality,
      );
    });
    return blob;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Could not process this photo.';
    throw new Error(
      `${message} On iPhone, try choosing “Most Compatible” / JPG, or pick a different image.`,
    );
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function loadHtmlImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not open this photo.'));
    img.src = src;
  });
}
